import { useCallback, useMemo, useState } from 'react';
import { uniq } from 'lodash';
import { Container, Row, Button } from '@geberit/gdds';

// types
import type { Tile, TilesColorVariant } from './tleb1-tiles-props';
import type { ChannelMainVideoProps } from '../video-channel/types';
import type { Items, Sizes } from './types';
import type { FilterElement, SelectableElements } from './tiles-filter/tiles-filter';

// styles
import styles from './tiles.module.scss';

// components
import Tleb1Tiles from './tleb1-tiles';
import { SectionClosingLine } from 'components/ContentElementsGdds/Common/section-closing-line';
import VideoLightbox from 'components/lightbox/video-lightbox';
import { InlineEdit } from 'components/ContentCreator/InlineEdit';
import { Headline } from '../headline/headline';
import { TilesFilter } from './tiles-filter/tiles-filter';

// constants
import { Formats } from 'components/ContentElementsGdds/headline/headlines.types';
import { THEME_NAMES } from 'themes';

// utils
import { isEmpty } from 'utils/is-empty';
import { clickTileLink } from 'components/ContentElements/teaser/tracking-actions';
import { classNameBuilder } from 'utils/classNameBuilder';
import { useTracking } from 'utils/hooks/useTracking';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useNord } from 'utils/hooks/use-nord';
import { carouselGridSize, gridSize } from './sizes';
import { useIsDesktop, useIsMobile, useIsTablet } from 'components/App/SizeProvider';
import { useSectionId } from 'utils/hooks/use-section-id';
import { useTranslationByKey, useTranslationFunction } from 'utils/hooks/use-translations';
import { useXy } from 'utils/hooks/use-xy';
import { isInArray } from 'utils/ts-utilities';
import { useThemeName } from 'utils/hooks/use-theme';
import { useSearchParamsState } from 'utils/hooks/use-search-params-state';
import { decodeValue } from 'utils/base64';
import { safeJsonParse } from '../../checkout/components/address-form/utils';

export type GddsProductiveTilesProps = {
  border: string;
  amount: number;
  aspectRatio: string;
  carousel: boolean;
  title: string;
  type: string;
  catalogName: string;
  titleInAnchor: boolean;
  sizes: Sizes;
  isSectionReference: boolean;
  anchor: string;
  subtitle: string;
  text: string;
  previewId: string;
  colorVariant: TilesColorVariant;
  isInSubscription: boolean;
  items: Items[];
  showLoadMoreButton: boolean;
  contentIndex?: number;
  contentLength?: number;
  background?: string;
  backgroundColor?: string;
  autoplay?: boolean;
  isChild?: boolean;
  childOf?: string;
};

export function GddsProductiveTiles(props: Readonly<GddsProductiveTilesProps>) {
  const {
    items = [],
    background = '',
    backgroundColor = '',
    title,
    subtitle,
    contentIndex,
    contentLength,
    text,
    carousel,
    type,
    sizes,
    border,
    colorVariant,
    anchor,
    titleInAnchor,
    aspectRatio,
    showLoadMoreButton,
    autoplay = false,
    isChild = false,
    childOf,
  } = props;

  const isXy = useXy();
  const theme = useThemeName();
  const isLargeDesktop = useIsDesktop({ gdds: true });
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const track = useTracking();
  const [modalOpen, setModalOpen] = useState<false | Partial<ChannelMainVideoProps>>(false);
  const isNord = useNord();
  const translate = useTranslationFunction();
  const isContactSection = type === 'contact_tiles';
  const loadMoreText = useTranslationByKey('web20_downloadcenter_more_results');
  const backgroundParam = background || backgroundColor || 'bg-white';
  const tileBackground = backgroundParam === 'white' ? 'light' : 'white';
  const sectionId = useSectionId(title ?? '', anchor ?? '', Boolean(titleInAnchor), contentIndex);
  const variant = determineProductVariant();
  const generateFilterItems = useCallback(() => {
    const filterElements: Record<SelectableElements, FilterElement> = {
      products: {
        label: 'filter_productfilter',
        placeholder: 'filter_placeholder',
        selectedText: 'filter_oneproduct_selected',
        selectedTextPlural: 'filter_multipleproduct_selected',
        items: [],
      },
      typeOfBuilding: {
        label: 'filter_typeofbuilding',
        placeholder: 'filter_placeholder',
        selectedText: 'filter_onetypeofbuilding_selected',
        selectedTextPlural: 'filter_multipletypeofbuilding_selected',
        items: [],
      },
      region: {
        label: 'filter_region',
        placeholder: 'filter_placeholder',
        selectedText: 'filter_oneregion_selected',
        selectedTextPlural: 'filter_multipleregion_selected',
        items: [],
      },
    };
    const filterNames = Object.keys(filterElements);
    filterNames.forEach((filterName) => {
      const filterItems = uniq(
        items
          .reduce((acc, item) => {
            const filterValue = (item.filters?.[filterName] ?? []) as string[];
            if (filterValue) {
              acc.push(filterValue);
            }
            return acc;
          }, [] as string[][])
          .flat(),
      ).map((value) => ({
        text: translate(value),
        value: translate(value),
        id: value,
      }));
      filterElements[filterName].items = filterItems;
    });

    return filterElements;
  }, [items, translate]);

  const filterElements = useMemo(generateFilterItems, [generateFilterItems]);
  const filterNames = Object.keys(filterElements);
  const decodedPreviewId = decodeValue(props.previewId);
  const fsSection = decodeValue(safeJsonParse(decodedPreviewId ?? '')?.['data-fs-id'] ?? '');
  const fsSectionId = safeJsonParse(fsSection ?? '')?.['id'] ?? '';
  const [searchParamsState] = useSearchParamsState(
    filterNames.map((e) => `${fsSectionId}_${e}`),
    [],
    true,
  );
  const isSomeFiltersEnabled = !isEmpty(Object.values(searchParamsState).flat().filter(Boolean));
  const [offset, setOffset] = useState(0);

  const loadableTiles = useMemo(() => {
    let tileSize: number | undefined;
    if (isMobile) {
      tileSize = sizes?.mobile;
    } else if (isTablet) {
      tileSize = sizes?.tablet;
    } else tileSize = sizes?.desktop;

    return tileSize;
  }, [isMobile, isTablet, sizes]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (isEmpty(Object.values(item?.filters ?? {}).flat()) || !isSomeFiltersEnabled) return true;
      const filterKeys = Object.keys(item?.filters ?? []);
      return filterKeys.every((filterName) => {
        const selectedFilters = searchParamsState[`${fsSectionId}_${filterName}`];
        if (isEmpty(selectedFilters)) return true;
        const filterValues = item.filters[filterName];
        return filterValues.some((filterValue) =>
          selectedFilters?.includes(translate(filterValue)),
        );
      });
    });
  }, [fsSectionId, isSomeFiltersEnabled, items, searchParamsState, translate]);

  const trackLink = (item: Tile) => {
    track.trackEvent(clickTileLink(window.location.href, item.link?.target));
  };

  const toggleVideoModal = (video) => {
    setModalOpen(video);
  };

  const isLastSection = (contentLength ?? 1) - 1 === contentIndex;

  // if (isEmpty(filteredItems)) return null;

  const displayLoadMoreButton = !carousel && showLoadMoreButton && !isSomeFiltersEnabled;
  const tilesToShow = displayLoadMoreButton
    ? filteredItems.slice(0, offset + loadableTiles)
    : filteredItems;

  const gridWidth =
    isLargeDesktop && carousel && tilesToShow.length > 3
      ? carouselGridSize['NORD']
      : gridSizes[gridSize['NORD']];

  return (
    <>
      <section
        className={classNameBuilder(
          styles.productiveWrapper,
          childOf === 'accordion' && styles.marginIsAccordion,
          childOf === 'tab' && styles.marginIsTab,
          isLastSection && styles.isLast,
          variant ? styles[variant] : '',
          !border ? styles.border : '',
        )}
        id={sectionId}
      >
        <InlineEdit
          previewId={(function () {
            if (isNord || isXy) {
              return '#st_tiles';
            }
            if (isContactSection) {
              return '#st_contacts';
            }
            return undefined;
          })()}
        >
          <Container
            maxContentWidth={buildSize(gridWidth)}
            className={classNameBuilder(
              styles.tilesWrapper,
              childOf === 'tab' && 'tab-child-container',
            )}
          >
            <Headline
              title={title}
              subtitle={subtitle}
              text={text}
              tag={isChild && isXy ? Formats.h4 : Formats.h2}
              variant={Formats.h2}
              className={styles.headline}
              textClassName={styles.copyText}
              titlePreviewId="#st_title"
              subtitlePreviewId="#st_subtitle"
            />
          </Container>
          <TilesFilter fsSectionId={fsSectionId} filterElements={filterElements} />
          <Container maxContentWidth={buildSize(gridWidth)} className={styles.tilesWrapper}>
            {!isEmpty(tilesToShow) && (
              <Row>
                <Tleb1Tiles
                  tiles={tilesToShow}
                  isMobile={isMobile}
                  toggleVideoModal={toggleVideoModal}
                  trackLink={trackLink}
                  isContactSection={isContactSection}
                  tileBackground={tileBackground}
                  carousel={Boolean(carousel)}
                  aspectRatio={aspectRatio}
                  colorVariant={variant}
                  autoplay={autoplay}
                  isChild={isChild}
                />
              </Row>
            )}
            {displayLoadMoreButton && tilesToShow?.length < filteredItems?.length && (
              <Row className={styles.buttonRow}>
                <Button
                  symbol="update"
                  className={styles.loadmoreIcon}
                  onClick={() => {
                    setOffset((prevOffset) => prevOffset + loadableTiles);
                  }}
                >
                  {loadMoreText}
                </Button>
              </Row>
            )}
          </Container>
        </InlineEdit>
        {!border && (
          <SectionClosingLine
            withChildAccordionMargin={childOf === 'accordion'}
            withChildTabMargin={childOf === 'tab'}
          />
        )}
      </section>
      {modalOpen && (
        <VideoLightbox
          onClose={toggleVideoModal}
          videoTitle={modalOpen?.title}
          videoId={modalOpen.video?.videoId ?? ''}
          isYoutubeVideo={modalOpen.video?.type === 'youtube'}
        />
      )}
    </>
  );

  function determineProductVariant() {
    if (type !== 'tiles') {
      return colorVariant;
    }

    let variant = colorVariant;
    const isXYOrKoloOrTwyford = isInArray(theme, [
      THEME_NAMES.XY,
      THEME_NAMES.KOLO,
      THEME_NAMES.TWYFORD,
    ]);
    if (
      (!['bg-white', 'bg-light-grey'].includes(variant) && isXYOrKoloOrTwyford) ||
      (variant === 'bg-light-grey' && !isXYOrKoloOrTwyford)
    ) {
      variant = 'bg-white';
    }
    return variant;
  }
}
